<template>
  <v-row
    class="fill-height"
    no-gutters
  >
    <BarChartTreeview
      :request-status="requestStatus"
      :series="series"
      :title="title"
      :item-id="itemId"
      :open-filter="openFilter"
      :open-dialog="openDialog"
      :has-custom-filter="true"
      :has-info-button="false"
      :card-options="cardOptions"
      v-bind="$attrs"
      @reload="getData"
    />
    <v-dialog
      v-model="filterDialog"
    >
      <v-card>
        <v-toolbar>
          <v-toolbar-title>
            {{ filterTitleLabel }}
          </v-toolbar-title>
          <v-spacer />
          <v-toolbar-items>
            <span>
              <StIcon
                class="close-icon"
                icon-name="close"
                @click="filterDialog = false"
              />
            </span>
          </v-toolbar-items>
        </v-toolbar>

        <v-row
          no-gutters
          class="mt-5 mx-5"
        >
          <TreeviewFilter
            v-if="filterDialog"
            :series="getOptionsTreeviewFilter()"
            :selected="selectedTags"
            class="mr-3"
            @filtered-items="setTags"
          />
        </v-row>

        <v-row
          no-gutters
          class="pb-5 px-5"
        >
          <v-spacer />
          <v-spacer />
          <v-btn
            outlined
            rounded
            class="mr-3"
            @click="clearFilter"
          >
            {{ $t('components.filter.ModalFilters.label.button.clearFilter') }}
          </v-btn>
          <v-btn
            outlined
            rounded
            @click="applyFilter"
          >
            {{ $t('components.filter.ModalFilters.label.button.applyLabel') }}
          </v-btn>
        </v-row>
      </v-card>
    </v-dialog>
    <v-dialog
      v-model="dialog"
    >
      <v-card>
        <v-card-actions class="close-button">
          <DsButton
            v-tooltip="closeLabel"
            color="tertiary"
            name="close"
            :icon="true"
            @click="dialog = false"
          />
        </v-card-actions>
        <v-card-text>
          <h3>{{ this.$t('components.SentmentByTagTreeview.faq.title') }}</h3>
          <p>{{ this.$t('components.SentmentByTagTreeview.faq.description') }}</p>

          <h3>{{ this.$t('components.SentmentByTagTreeview.faq2.title') }}</h3>
          <p>{{ this.$t('components.SentmentByTagTreeview.faq2.description') }}</p>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-row>
</template>

<script>
import BarChartTreeview from './models/BarChartTreeview.vue';
import sentimentModel from '@/mixins/sentiment-model';
import cardsModel from '@/mixins/cards-model';
import { POST_FIELDS, OPERATION } from '@/services/charts-service';
import EventBus from '@/utils/event-bus';
import { mapActions, mapGetters } from 'vuex';
import highchartsBasics from '@/mixins/highcharts-basics';
import TreeviewFilter from '@/components/cards/models/TreeviewFilter.vue';
import { DsButton } from '@ds/components';
import { cloneDeep } from 'lodash';

export default {
  name: 'SentimentByTagTreeviewFilter',
  components: {
    BarChartTreeview,
    TreeviewFilter,
    DsButton,
  },
  mixins: [
    sentimentModel,
    cardsModel,
    highchartsBasics,
  ],
  props: {
    title: {
      type: String,
      required: true,
    },
    itemId: {
      type: [String, Number],
      required: true,
    },
    operation: {
      type: String,
      default: OPERATION.COUNT,
    },
    field: {
      type: String,
      default: null,
    },
    group: {
      type: String,
      required: true,
    },
    label: {
      type: String,
      default: null,
    },
    hideNull: {
      type: Boolean,
      default: false,
    },
    polarity: {
      type: Boolean,
      default: true,
    },
    stackLabels: {
      type: [Boolean, String],
      default: true,
    },
    filterName: {
      type: String,
      default: null,
    },
    isExpandedCard: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      requestStatus: 'loading',
      filterButton: this.$t('components.filter.ModalFilters.label.button.applyLabel'),
      filterTitle: this.$t('components.SentimentByTagTreeviewFilter.label.filterTitle'),
      rawData: [],
      tagListForTreeview: [],
      pointsParser: 'points',
      filter: {},
      tempFilter: {},
      selectedTags: [],
      filterDialog: false,
      dialog: false,
      closeLabel: this.$t('components.SentmentByTagTreeview.closeLabel'),
      cardOptions: {
        yAxis: {
          stackLabels: {
            enabled: Boolean(this.stackLabels),
            ...(this.stackLabels === 'percentage'
              ? { formatter: (component => {
                return function() {
                  return `${component.sumSeries
                    ? ((this.total / component.sumSeries) * 100).toFixed(1)
                    : 0}%`;
                };
              })(this) }
              : null),
          },
        },
        plotOptions: {
          series: {
            stacking: 'normal',
          },
        },
      },
    };
  },
  computed: {
    ...mapGetters('dashboard', [
      'activeDashboardFilters',
      'activeDashboard',
    ]),
    series() {
      const filteredValues = this.filter[this.filterName]?.value;
      const { pointsParser } = this;
      const data = [...this.rawData];

      let rawSeries = [];
      if (this.label) {
        data.forEach(p => {
          p.y = this.label;
          return p;
        });
      }
      if (filteredValues) {
        const filteredSeries = data?.filter(point => {
          return filteredValues.includes(point.x) || filteredValues.includes(point.y);
        }) || [];
        rawSeries = this.normalizeSentimentResponseFromChartService(filteredSeries, pointsParser);
      }
      rawSeries = this.normalizeSentimentResponseFromChartService(data, pointsParser, this.hideNull);

      if (!this.isExpandedCard) {
        return this.normalizeSeries(rawSeries);
      }

      return rawSeries;
    },
    sumSeries() {
      return this.series.reduce((sum, serie) => {
        return sum + serie.data.reduce((sum, point) => sum + point.y, 0);
      }, 0);
    },
    getOptions() {
      return this.activeDashboardFilters?.tags || [];
    },
    filterTitleLabel() {
      return `Filtro - ${this.filterTitle}`;
    },
  },
  created() {
    this.cloneCardFilter();
    this.getData();
  },
  methods: {
    ...mapActions('cards', [
      'getChartsService',
      'getWarroomService',
    ]),
    getData(options) {
      if (!options?.silentRefresh) {
        this.requestStatus = 'loading';
      }
      this.applyFilter(options);
    },
    applyFilter(options) {
      this.requestStatus = 'loading';
      const grouping = [this.group];
      if (this.polarity && !this.field) {
        grouping.push(POST_FIELDS.POLARITY);
      }

      if (this.$attrs.filter) {
        this.filter = this.$attrs.filter;
      }

      if (options?.silentRefresh) {
        EventBus.$emit('updated', { itemId: this.itemId });
      }

      this.getChartsService({
        itemId: this.itemId,
        method: 'getAggregatedData',
        points_parser: this.pointsParser,
        params: {
          grouping,
          operators: this.operation,
          ...(this.field ? { fields: [this.field] } : null),
          datePortion: this.getDatePortion(options),
        },
      })
        .then((response) => {
          this.prepareData(response?.points || []);
          this.card.props.filter = this.tempFilter;
          this.saveCardFilter();
          this.requestStatus = 'success';
          this.filterDialog = false;
        })
        .catch((err) => {
          console.error(err);
          this.requestStatus = 'error';
        });
    },
    normalizeSeries(series) {
      if (series.length === 1) {
        series[0].data.sort((first, second) => first.x - second.x);

        return [{
          ...series[0],
          data: series[0].data?.slice(0, this.maxItemsToBeDisplayed),
        }];
      }

      return series.map(serie => {
        const newData = serie.data.filter(each => {
          return each.x < this.maxItemsToBeDisplayed;
        });

        return {
          ...serie,
          data: newData,
        };
      });
    },
    openFilter() {
      this.filterDialog = true;
    },
    openDialog() {
      this.dialog = true;
    },
    setTags(tags) {
      this.selectedTags = tags.slice();
      this.tempFilter.tags = { value: this.selectedTags };
    },
    clearFilter() {
      delete this.card.props.filter.tags;
      this.saveCardFilter();
      this.tempFilter = {};
      this.selectedTags = [];
      this.applyFilter();
    },
    prepareData(response) {
      const responseData = response;
      this.rawData = response;

      if (this.selectedTags.length > 0) {
        const filteredData = [];
        responseData.forEach((tag) => {
          if (this.selectedTags.find(element => element === tag.x)) {
            filteredData.push(tag);
          }
        });

        this.rawData = filteredData;
      }
    },
    getOptionsTreeviewFilter() {
      const returnList = [];
      this.getOptions.forEach((tag) => {
        const parts = tag.split(' ');
        let currentNode = returnList;
        let currentId = '';
        parts.forEach((part, index) => {
          currentId += (index > 0 ? ' ' : '') + part;
          const existingNode = currentNode.find((node) => node.id === currentId);
          if (existingNode) {
            currentNode = existingNode.children;
          } else {
            const newNode = {
              id: currentId,
              name: part,
              children: [],
              checked: true,
            };
            currentNode.push(newNode);
            currentNode = newNode.children;
          }
        });
      });
      return returnList;
    },
    cloneCardFilter() {
      if ('filter' in this.card.props) {
        this.tempFilter = cloneDeep(this.card.props.filter);
        this.selectedTags = this.tempFilter.tags?.value || [];
      }
    },
    saveCardFilter() {
      this.saveCards().then(() => {
        this.showExtraOptions = false;
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.close-button {
  display: flex;
  flex: end;
  justify-content: flex-end;
}

::v-deep {
  .v-dialog {
    width: 40%;
    height: 50%;
  }
}
</style>
